import React, { FC, useContext, useMemo, useState } from 'react';

import Image from 'next/image';
import { useTranslation } from 'next-i18next/pages';

import { Button, Tooltip } from 'antd';
import cn from 'classnames';

import { createPaymentRequest } from '@/api/tariff';
import GoldFishIcon from '@/assets/gold-fish-icon.svg';
import IncludedIcon from '@/assets/included-icon.svg';
import PromptIcon from '@/assets/prompt-icon.svg';
import UnincludedIcon from '@/assets/unincluded-icon.svg';
import { getLocalizedTariffName } from '@/components/TariffModal/utils';
import { ApplicationPaymentType } from '@/constants/application-payments';
import { FeedbackContext } from '@/context/feeadback.context';
import { TranslationNamespace } from '@/i18n';
import { Tariff } from '@/types/tariff';

import { InvoiceModal } from '../InvoiceModal/InvoiceModal';
import { InvoiceFieldType } from '../InvoiceModalContent/InvoiceModalContent';
import { TariffsTableProps } from '../types';
import { getTariffsInitialState } from '../utils';

import styles from './TariffsTableDesktop.module.scss';

export const TariffsTableDesktop: FC<TariffsTableProps> = ({
	tariffsData,
	showButtons,
	userIsAuth,
	onTariffChoice,
	tariffChoiceDisable,
	isActiveTariff,
	currentTariff,
}) => {
	const [isShowMore, setIsShowMore] = useState(true);
	const [isOpenModal, setIsOpenModal] = useState(false);
	const [slicedItems, setSlicedItems] = useState(getTariffsInitialState(tariffsData));
	const [isLoading, setIsLoading] = useState(false);
	const { notify } = useContext(FeedbackContext);
	const [selectedTariff, setSelectedTariff] = useState<Tariff.TariffWithSettings>(null);

	const tableRef = React.useRef<HTMLDivElement>(null);

	const t = useTranslation(TranslationNamespace.Marketing).t;

	const vipTariff = useMemo(() => {
		return tariffsData?.tariffs.find((tariff) => tariff.applyOnRequest);
	}, [tariffsData]);

	const showModal = () => {
		setIsOpenModal(true);
	};

	const hideModal = () => {
		setIsOpenModal(false);
	};

	const handleClickTariff = (params?: {
		tariffInfo: Tariff.TariffWithSettings;
		perYear?: boolean;
		per_three_month?: boolean;
	}) => {
		if (params.tariffInfo.applyOnRequest) {
			showModal();
		} else {
			onTariffChoice?.(params);
		}
	};

	const handleSendInvoice = async (formData: InvoiceFieldType) => {
		const { name, phone, email, comment } = formData;

		try {
			setIsLoading(true);

			await createPaymentRequest({
				type: ApplicationPaymentType.Tariff,
				infoForRequest: {
					name,
					phone,
					email,
					comment,
				},
				tariff: vipTariff.id,
			});

			notify.success({
				title: t('tariffs-table.your-application-has-been-successfully-sent'),
			});
			hideModal();
		} catch (error) {
			notify.error({
				title: error?.message ?? t('tariffs-table.an-error-occurred-while-sending-the-application'),
				description: t('tariffs-table.please-try-again'),
			});
		} finally {
			setIsLoading(false);
		}
	};
	const PER_THREE_MOTH_TARIFFS = [Tariff.TariffId.Basic, Tariff.TariffId.Silver];

	const getButton = (tariff: Tariff.TariffWithSettings, text: string) => {
		return (
			<Button
				className={styles.choiceButton}
				type="primary"
				size="large"
				onClick={() => {
					setSelectedTariff(tariff);

					const lifetime = PER_THREE_MOTH_TARIFFS.includes(tariff.id)
						? {
								per_three_month: true,
							}
						: {
								perYear: true,
							};

					handleClickTariff({ tariffInfo: tariff, ...lifetime });
				}}
				disabled={tariffChoiceDisable}
			>
				{text}
			</Button>
		);
	};

	const renderButtons = (tariff: Tariff.TariffWithSettings) => {
		const tariffButtonText =
			tariff.id === Tariff.TariffId.PlatinumDiamond
				? 'tariffs-table.request'
				: 'tariffs-table.choose';

		if (!showButtons) return null;

		if (!userIsAuth) {
			return getButton(tariff, t(tariffButtonText));
		}

		if ((currentTariff && !isActiveTariff) || !currentTariff) {
			return getButton(tariff, t(tariffButtonText));
		}

		if (currentTariff === tariff.id) {
			return <p className={styles.currentTariff}>{t('tariffs-table.current-tariff')}</p>;
		}

		return getButton(tariff, t('tariffs-table.change'));
	};

	const renderParameterCell = (setting: Tariff.Setting, isFirstItem: boolean) => {
		if (typeof setting?.value === 'string') {
			const isWithoutCommission = ['без комиссии', 'without commission'].includes(
				setting.value.toLowerCase(),
			);

			return isWithoutCommission ? (
				<div className={styles.withoutCommission}>{t('tariffs-table.without-commission')}</div>
			) : (
				<>
					<div
						className={cn(styles.settingValue, {
							[styles.boldSettingValue]: isFirstItem,
						})}
					>
						{setting.value}
					</div>
					{setting.description && (
						<div className={styles.settingDescription}>{setting.description}</div>
					)}
				</>
			);
		}

		return setting?.value ? (
			<Image width={24} height={24} alt="" src={IncludedIcon} />
		) : (
			<Image width={24} height={24} alt="" src={UnincludedIcon} />
		);
	};

	const handleClickShowMore = () => {
		if (isShowMore) {
			setSlicedItems(tariffsData?.tariffsInfo);
		} else {
			setSlicedItems(getTariffsInitialState(tariffsData));
			tableRef.current?.scrollIntoView({ block: 'start' });
		}

		setIsShowMore(!isShowMore);
	};

	return (
		<div className={styles.table} ref={tableRef}>
			<div className={styles.header}>
				<div className={styles.headerCell} key="head-cell">
					<h2 className={styles.title}>{t('tariffs-table.tariffs')}</h2>
					<div className={styles.tariffDate}>{t('tariffs-table.effective-from-may-8-2026')}</div>
				</div>
				{tariffsData?.tariffs.map((tariff) => {
					return (
						<div
							key={tariff.id}
							className={cn(styles.tariffCell, {
								[styles.bestChoiceColumn]: tariff.bestChoice,
							})}
						>
							<div className={styles.tariffName}>
								{tariff.bestChoice && (
									<div className={styles.bestChoiceLabel}>
										{t('tariffs-table.choose-suppliers')}
									</div>
								)}
								{getLocalizedTariffName(tariff.id, tariff.name, t)}
								{tariff.id === Tariff.TariffId.PlatinumDiamond && (
									<Image
										className={styles.goldFishIcon}
										width={24}
										height={24}
										alt=""
										src={GoldFishIcon}
									/>
								)}
							</div>
							<div className={styles.tariffDescription}>{tariff.prompt}</div>
						</div>
					);
				})}
			</div>
			<div className={styles.body}>
				<div className={styles.settingsColumn}>
					{slicedItems.map((tariffInfo) => (
						<div key={tariffInfo.id} className={styles.labelCell}>
							<div>{tariffInfo.name}</div>
							{tariffInfo.prompt && (
								<Tooltip title={tariffInfo.prompt}>
									<Image className={styles.hint} src={PromptIcon} alt="" />
								</Tooltip>
							)}
						</div>
					))}
				</div>
				{tariffsData?.tariffs.map((tariff) => {
					return (
						<div
							key={tariff.name}
							className={cn(styles.infoColumns, {
								[styles.bestChoiceColumn]: tariff.bestChoice,
							})}
						>
							{slicedItems.map((tariffInfo, index) => {
								const isFirstItem = index === 0;
								const tariffSetting = tariff.settings.find((item) => item.infoId === tariffInfo.id);

								if (!tariffSetting) {
									return (
										<div key={tariffInfo.name + index} className={styles.labelSetting}>
											<Image width={24} height={24} alt="" src={UnincludedIcon} />
										</div>
									);
								}

								return (
									<div
										key={`${tariffSetting?.description}${tariffSetting?.infoId}${index}`}
										className={styles.labelSetting}
									>
										{renderParameterCell(tariffSetting, isFirstItem)}
									</div>
								);
							})}
						</div>
					);
				})}
				<div className={styles.showMoreButtonWrapper}>
					<Button
						type="text"
						className={styles.showMoreButton}
						size="large"
						onClick={handleClickShowMore}
					>
						{t(isShowMore ? 'tariffs-table.show-all-benefits' : 'tariffs-table.hide-benefits')}
					</Button>
				</div>
				{tariffsData?.tariffs?.map((tariff) => {
					return (
						<div
							key={tariff.name}
							className={cn(styles.infoColumns, {
								[styles.bestChoiceColumn]: tariff.bestChoice,
								[styles.buttonWrapper]: !tariff.bestChoice,
							})}
						>
							{renderButtons(tariff)}
						</div>
					);
				})}
			</div>
			<div className={styles.infoDescription}>
				{t(
					'tariffs-table.you-can-get-acquainted-with-the-detailed-terms-of-the-tariff-by-contacti',
				)}
			</div>
			{isOpenModal && (
				<InvoiceModal
					isLoading={isLoading}
					onSendInvoice={handleSendInvoice}
					isOpenModal={isOpenModal}
					title={t('tariffs-table.tariff-request')}
					onHideModal={hideModal}
					description={t('tariffs-table.vip-description')}
				/>
			)}
		</div>
	);
};
